// Chart color palette and shared chart constants
import { CHART_CONSTANTS } from 'src/app/app.constants';
import { ColDef, RowClassRules } from 'ag-grid-community';

/**
 * Centralized constants for deviation survey feature.
 * Includes chart, validation, file, and rod type settings.
 */
export const DEVIATION_SURVEY_CONSTANTS = {
  DEFAULT_MARKER_DEPTH: -1, // Default depth for pump/anchor markers (ft)
  // Step size for chart and calculations (ft)
  DEFAULT_STEP_SIZE: 50,
  // Zone limits for severity coloring (ft)
  DEFAULT_GREEN_ZONE_LIMIT: 200,
  DEFAULT_BLUE_ZONE_LIMIT: 400,
  // Maximum allowed rod zones and chart points
  MAX_ALLOWED_ZONES: 200,
  MAX_CHART_POINTS: 5000,
  // Highcharts turboThreshold for the trajectory, dog-leg and side-load series — 0 disables
  // turbo mode so those series always accept object-shaped points.
  MAX_THRESHOLD: 0,
  // Chart colors for severity and rods
  GREEN_ZONE_COLOR: '#90ed7d',
  BLUE_ZONE_COLOR: '#7cb5ec',
  RED_ZONE_COLOR: '#f45b5b',
  NO_RODS_COLOR: 'silver',
  // Initial values for survey calculations
  INITIAL_ALPHA: 9.5,
  INITIAL_BETA: 45,
  // Highcharts color palette (shared)
  HIGHCHART_COLORS: CHART_CONSTANTS.COLORS,
  // Rod type dictionary for display
  TYPE_DICTIONARY: {
    0: 'Steel',
    1: 'High Strength Steel',
    2: 'Sinker Bar',
    3: 'Fiberglass',
  } as { [key: number]: string },
  // Column names for grid/chart display
  COLUMN_NAMES: {
    MD: 'MD (ft)',
    INC: 'Incl (deg)',
    AZ: 'Az (deg)',
    NS: 'N-S (ft)',
    EW: 'E-W (ft)',
    TVD: 'TVD (ft)',
    DLS: 'DLS (deg)',
    SL: 'SL (lbs)',
  },
  // Validation rules for survey data and file upload
  VALIDATIONS: {
    maxAllowedRows: 30000, // Maximum allowed rows in the deviation survey
    maxFileSizeInBytes: 10 * 1024 * 1024, // 10MB
    minAllowedDepth: 0,
    maxAllowedDepth: 100000,
    mdDelta: 1,
    minDegree: 0,
    maxIncDegree: 180,
    maxAzDegree: 359.99,
  },
  // File suffix for survey CSV export/import
  FILE_SUFFIX: '-survey.csv',

  SERIES_ID: {
    STEPPED_TRAJECTORY: 'steppedTrajectory',
    WELLHEAD_MARKER: 'wellheadMarker',
    PUMP_MARKER: 'pumpMarker',
    ANCHOR_MARKER: 'anchorMarker',
    COMPASS_MARKER: 'compassMarker',
    TRAJECTORY: 'trajectory',
    ROD_TYPE: 'rodType',
  },
  SERIES_INDEX: {
    STEPPED_TRAJECTORY: 0,
    WELLHEAD_MARKER: 1,
    PUMP_MARKER: 2,
    ANCHOR_MARKER: 3,
    COMPASS_MARKER: 4,
    TRAJECTORY: 5,
  },
};

const { minAllowedDepth, maxAllowedDepth, minDegree, maxAzDegree, maxIncDegree } =
  DEVIATION_SURVEY_CONSTANTS.VALIDATIONS;

export const DEVIATION_SURVEY_GRID_CONSTANTS = {
  NUMBER_FILTER_PARAMS: {
    filterOptions: [
      'equals',
      'lessThan',
      'lessThanOrEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'inRange',
    ],
    buttons: ['clear'],
    suppressAndOrCondition: true,
  },

  MEASURED_DEPTH_FILTER_PARAMS: {
    filterOptions: [
      'equals',
      'lessThan',
      'lessThanOrEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'inRange',
      {
        displayKey: 'hasError',
        displayName: 'Has Error',
        predicate: (_: any[], cellValue: number) =>
          cellValue == null ||
          isNaN(cellValue) ||
          cellValue < minAllowedDepth ||
          cellValue > maxAllowedDepth,
        numberOfInputs: 0,
      },
    ],
    defaultOption: 'greaterThanOrEqual',
    filterPlaceholder: `${minAllowedDepth} to ${maxAllowedDepth}`,
    buttons: ['clear'],
    suppressAndOrCondition: true,
  },

  AZ_DEGREE_FILTER_PARAMS: {
    filterOptions: [
      'equals',
      'lessThan',
      'lessThanOrEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'inRange',
      {
        displayKey: 'hasError',
        displayName: 'Has Error',
        predicate: (_: any[], cellValue: number) =>
          cellValue == null || isNaN(cellValue) || cellValue < minDegree || cellValue > maxAzDegree,
        numberOfInputs: 0,
      },
    ],
    defaultOption: 'greaterThanOrEqual',
    filterPlaceholder: `${minDegree} to ${maxAzDegree}`,
    buttons: ['clear'],
    suppressAndOrCondition: true,
  },

  INC_DEGREE_FILTER_PARAMS: {
    filterOptions: [
      'equals',
      'lessThan',
      'lessThanOrEqual',
      'greaterThan',
      'greaterThanOrEqual',
      'inRange',
      {
        displayKey: 'hasError',
        displayName: 'Has Error',
        predicate: (_: any[], cellValue: number) =>
          cellValue == null ||
          isNaN(cellValue) ||
          cellValue < minDegree ||
          cellValue > maxIncDegree,
        numberOfInputs: 0,
      },
    ],
    defaultOption: 'greaterThanOrEqual',
    filterPlaceholder: `${minDegree} to ${maxIncDegree}`,
    buttons: ['clear'],
    suppressAndOrCondition: true,
  },

  ROW_CLASS_RULES: {
    'invalid-input-row': (params: any) => params.data.rowHasError,
  } as RowClassRules,

  DEFAULT_COL_DEF: {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 120,
    cellClass: 'ag-no-wrap-cell',
    headerClass: 'ag-header-cell',
    singleClickEdit: true,
    cellStyle: { 'text-align': 'right' },
  } as ColDef,
};
